import React, { createContext, useState, useContext, useEffect } from 'react';
import cloneDeep from 'lodash.clonedeep';
import { Environment } from 'src/Environment';
import {
  SalesforceContext,
  AcquiringChannelFlags,
  CustomerTypes,
  ecommerceProductCategoryName,
} from './salesforce-context';
import toastMessageServiceSingleton from 'src/services/toast-message.service';
import { vatPercent } from 'src/helpers/constants';
import { isVirtualorInstoreDevice } from 'src/helpers/devices';
import { getValue, urlQueryParams } from 'src/helpers/custom-url-search-param';

export const OnboardingSummaryContext = createContext();

export function OnboardingSummaryProvider({ children, toggleNext }) {
  const {
    lead,
    merchant,
    terminals,
    getDeviceOrderQuantity,
    deviceDeliveryDetails,
    setPaymentGatewayAmount,
    isLoadingFromSessionStorage,
    customerType,
  } = useContext(SalesforceContext);

  //////////////////////////////////////

  const [deviceQuantityToUpdate, setDeviceQuantityToUpdate] = useState(null);
  const [deviceIdToUpdate, setDeviceIdToUpdate] = useState(null);
  const [devicePriceToUpdate, setDevicePriceToUpdate] = useState(null);
  const [deviceQuantityIsManualUpdate, setDeviceQuantityIsManualUpdate] = useState(null);

  const [totalNumberOfDevices, setTotalNumberOfDevices] = useState(0);
  const [vatPercentage, setVatPercentage] = useState(vatPercent);

  const [totalPrice, setTotalPrice] = useState(null);
  const [onceOffCost, setOnceOffCost] = useState(null);
  const [hasBuyInstoreMixture, setHasBuyInstoreMixture] = useState();
  const [instoreDeviceName, setInstoreDeviceName] = useState();

  //////////////////////////////////////

  useEffect(() => {
    if (isLoadingFromSessionStorage) {
      return;
    }

    setHasBuyInstoreMixture(
      customerType === CustomerTypes.agent &&
        !!getDeliveriesToDisplay().some(
          (delivery) => delivery.acquiring_channel === AcquiringChannelFlags.buyInstoreFicaOnline,
        ),
    );

    const total = getDeliveriesToDisplay()
      .map((delivery) => {
        const priceExclVAT =
          delivery.acquiring_channel === AcquiringChannelFlags.rentOnlineFicaOnline ||
          delivery.acquiring_channel === AcquiringChannelFlags.zeroRentOnlineFicaOnline
            ? delivery.price * getDeviceOrderQuantity(delivery.device_id)
            : 0;
        const VAT = priceExclVAT * vatPercent;
        return priceExclVAT + VAT;
      })
      .reduce((a, b) => a + b, 0);

    const onceOffTotal = getDeliveriesToDisplay()
      .map((delivery) => {
        const priceExclVAT =
          delivery.acquiring_channel === AcquiringChannelFlags.buyOnlineFicaOnline
            ? delivery.price * getDeviceOrderQuantity(delivery.device_id)
            : 0;
        const VAT = priceExclVAT * vatPercent;
        return priceExclVAT + VAT;
      })
      .reduce((a, b) => a + b, 0);

    setInstoreDeviceName(
      getDeliveriesToDisplay().find(
        (delivery) => delivery.acquiring_channel === AcquiringChannelFlags.buyInstoreFicaOnline,
      )?.device_name,
    );

    setOnceOffCost(onceOffTotal);
    setPaymentGatewayAmount(onceOffTotal);
    setTotalPrice(total);

    let deviceQuantitiesToDisplayValue = deviceQuantitiesToDisplay();

    let totalDevices = deviceQuantitiesToDisplayValue
      .flatMap((delivery) => Object.values(delivery.devices))
      .reduce((quantityA, quantityB) => quantityA + quantityB, 0);
    setTotalNumberOfDevices(totalDevices);
  }, [merchant, isLoadingFromSessionStorage]);

  useEffect(() => {
    if (isLoadingFromSessionStorage) {
      return;
    }

    if (totalNumberOfDevices > 70) {
      toastMessageServiceSingleton.addError(
        'Maximum 70 terminals in total, please adjust your quantity.',
        null,
        'N/A',
        true,
      );
      toggleNext(false);
    } else {
      const qp = urlQueryParams(window.location.search);
      if (getValue(qp, 'origin') !== 'VPG' && !merchant?.opportunity?.online_payment_order_no) {
        toggleNext(true);
      }
    }
  }, [totalNumberOfDevices, isLoadingFromSessionStorage]);

  ////////////////////////////////////////////////

  const deviceQuantitiesToDisplay = () => {
    // work out which devices have no delivery address
    let merchantDeliveriesCopy = cloneDeep(merchant?.opportunity?.deliveries);
    let deliveriesWithAddressCopy = cloneDeep(deviceDeliveryDetails);
    const deliveriesToDefaultAddress =
      deliveriesWithAddressCopy.reduce((accum, delivery) => {
        Object.keys(delivery.devices).forEach((terminalId) => {
          if (accum[terminalId]) {
            accum[terminalId].number_of_devices -= delivery.devices[terminalId];
          }
        });
        return accum;
      }, merchantDeliveriesCopy) || {};

    if (
      Object.values(deliveriesToDefaultAddress).filter(
        (delivery) =>
          delivery.number_of_devices > 0 &&
          !isVirtualorInstoreDevice(delivery.acquiring_channel) &&
          delivery.product_category !== ecommerceProductCategoryName,
      ).length > 0
    ) {
      const deliveryDefaultAddress = {
        address_type: 'business',
        manager_name: merchant?.contact?.first_names || lead?.firstNames,
        id_number: merchant?.contact?.id_number,
        trading_as: merchant?.account?.trading_as,
        phone_work: merchant?.contact?.phone,
        phone_cell: lead?.mobileNumber,
        street: merchant?.account?.street,
        suburb: merchant?.account?.suburb,
        city: merchant?.account?.city,
        region: merchant?.account?.region,
        state: merchant?.account?.state,
        zip_code: merchant?.account?.zip_code,
        gps_location: merchant?.account?.gps_location,
        devices: Object.values(deliveriesToDefaultAddress).reduce((accum, element) => {
          const numDevices = parseInt(element.number_of_devices);
          if (
            numDevices > 0 &&
            !isVirtualorInstoreDevice(element.acquiring_channel) &&
            element.product_category !== ecommerceProductCategoryName
          ) {
            accum[element.device_id] = numDevices;
          }
          return accum;
        }, {}),
      };
      return [deliveryDefaultAddress].concat(deviceDeliveryDetails);
    }

    return deviceDeliveryDetails;
  };

  const getDeliveriesToDisplay = () =>
    (merchant?.opportunity &&
      merchant?.opportunity?.deliveries &&
      Object.values(merchant.opportunity.deliveries).filter(
        (delivery) => delivery.number_of_devices > 0,
      )) ||
    [];

  const getDeviceIdByName = (deviceName) => {
    const filteredArray = Object.values(terminals).filter(
      (term) => term.name.toLowerCase() === deviceName.toLowerCase(),
    );
    if (filteredArray.length > 0) {
      return filteredArray[0].id;
    } else {
      return '';
    }
  };

  const isChopChop = (deviceId) => {
    const chopChopName = Environment.current?.chopchopName;
    const chopChopDeviceId = getDeviceIdByName(chopChopName);
    return deviceId === chopChopDeviceId;
  };

  return (
    <OnboardingSummaryContext.Provider
      value={{
        isChopChop,
        deviceIdToUpdate,
        setDeviceIdToUpdate,
        deviceQuantityToUpdate,
        setDeviceQuantityToUpdate,
        devicePriceToUpdate,
        setDevicePriceToUpdate,
        deviceQuantityIsManualUpdate,
        setDeviceQuantityIsManualUpdate,
        vatPercentage,
        setVatPercentage,
        totalPrice,
        setTotalPrice,
        getDeliveriesToDisplay,
        deviceQuantitiesToDisplay,
        totalNumberOfDevices,
        onceOffCost,
        customerType,
        hasBuyInstoreMixture,
        instoreDeviceName,
      }}
    >
      {children}
    </OnboardingSummaryContext.Provider>
  );
}
